import React from 'react'
import {View} from 'react-native'
import Markdown from 'react-native-markdown-display'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'

import {usePalette} from '#/lib/hooks/usePalette'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {s} from '#/lib/styles'
import {useSetMinimalShellMode} from '#/state/shell'
import {ViewHeader} from '#/view/com/util/ViewHeader'
import {CenteredView} from '#/view/com/util/Views'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {atoms as a, useTheme} from '#/alf'
import {Flag_Stroke2_Corner0_Rounded as Flag} from '#/components/icons/Flag'
import {Newspaper_Stroke2_Corner2_Rounded as Newspaper} from '#/components/icons/Newspaper'
import {Person_Stroke2_Corner2_Rounded as Person} from '#/components/icons/Person'
import {Shield_Stroke2_Corner0_Rounded as Shield} from '#/components/icons/Shield'
import {Ticket_Stroke2_Corner0_Rounded as Ticket} from '#/components/icons/Ticket'
import {VideoCamera_Filled_Corner0_Rounded as VideoCamera} from '#/components/icons/VideoCamera'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {helpCategories} from '#/content/help'
import {supportContent} from '#/content/support'

const categoryIcons: Record<string, React.ComponentType<any>> = {
  account: Person,
  billing: Ticket,
  reporting: Flag,
  safety: Shield,
  'batesky-features': VideoCamera,
}

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Support'>
export const SupportScreen = (_props: Props) => {
  const pal = usePalette('default')
  const {_} = useLingui()
  const t = useTheme()
  const setMinimalShellMode = useSetMinimalShellMode()

  useFocusEffect(
    React.useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  const markdownStyles = {
    body: {
      color: pal.text.color,
      fontSize: 16,
      lineHeight: 24,
    },
    heading1: {
      fontSize: 28,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    heading2: {
      fontSize: 24,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    heading3: {
      fontSize: 20,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    paragraph: {
      marginBottom: 10,
      color: pal.text.color,
    },
    listItem: {
      marginBottom: 5,
      color: pal.text.color,
    },
    listItemBullet: {
      color: pal.text.color,
    },
    strong: {
      fontWeight: 'bold' as const,
    },
    em: {
      fontStyle: 'italic' as const,
    },
    link: {
      color: pal.link.color,
    },
    hr: {
      backgroundColor: pal.border.borderColor,
      height: 1,
      marginVertical: 20,
    },
  }

  return (
    <Layout.Screen>
      <ViewHeader title={_(msg`Support`)} />
      <CenteredView>
        <View style={[s.p20]}>
          <Markdown style={markdownStyles}>{supportContent}</Markdown>
        </View>

        {/* Info Hub Section */}
        <View style={[s.p20]}>
          <Text
            style={[
              {fontSize: 28, marginTop: 20, marginBottom: 10},
              a.font_bold,
              t.atoms.text_contrast_high,
            ]}>
            <Trans>Info Hub</Trans>
          </Text>
        </View>
        {helpCategories.map(category => {
          const Icon = categoryIcons[category.slug] || Person
          return (
            <View key={category.slug} style={[a.pb_xl]}>
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.gap_sm,
                  a.px_xl,
                  a.pb_xs,
                ]}>
                <Icon size="md" style={[t.atoms.text_contrast_medium]} />
                <Text
                  style={[
                    a.text_md,
                    a.font_bold,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {category.title}
                </Text>
              </View>
              <SettingsList.Container>
                {category.articles.map(article => (
                  <SettingsList.LinkItem
                    key={article.slug}
                    to={`/support/${category.slug}/${article.slug}`}
                    label={article.title}>
                    <SettingsList.ItemText>
                      {article.title}
                    </SettingsList.ItemText>
                  </SettingsList.LinkItem>
                ))}
              </SettingsList.Container>
            </View>
          )
        })}

        {/* Guidelines and Policies Section (Settings Style) */}
        <View style={[s.p20]}>
          <Text
            style={[
              {fontSize: 28, marginTop: 20, marginBottom: -15},
              a.font_bold,
              t.atoms.text_contrast_high,
            ]}>
            <Trans>Guidelines and Policies</Trans>
          </Text>
        </View>
        <View style={[a.pb_2xl]}>
          <SettingsList.Container>
            <SettingsList.LinkItem
              to="/support/community-guidelines"
              label={_(msg`Community Guidelines`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>Community Guidelines</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
            <SettingsList.LinkItem
              to="/support/tos"
              label={_(msg`Terms of Service`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>Terms of Service</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
            <SettingsList.LinkItem
              to="/support/privacy-policy"
              label={_(msg`Privacy Policy`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>Privacy Policy</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
            <SettingsList.LinkItem
              to="/support/copyright"
              label={_(msg`Copyright Policy`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>Copyright Policy</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
            <SettingsList.LinkItem
              to="/support/2257"
              label={_(msg`2257 Statement`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>2257 Statement</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
          </SettingsList.Container>
        </View>
        <View style={{height: 100}} />
      </CenteredView>
    </Layout.Screen>
  )
}
